import { USER_STATUS, userStatusColorMap } from "Common/dictionaries/userStatus";
import Button from "Components/button";
import ColorStyledBlock, { AREA } from "Components/colorStyledBlock";
import { DateCell, TextCell, clearSelection } from "Components/dataGridBase";
import ProgressOverlay from "Components/progressOverlay";
import ScrolledDropdown from "Components/scrolledDropdown";
import WelcomePanel, { emptyContentIcon } from "Components/welcomePanel";
import YesNoConfirmation from "Components/yesNoConfirmation";
import classNames from "classnames";
import { Column, Editing, HeaderFilter, Lookup, Toolbar, Item as ToolbarItem, EmailRule, RequiredRule, CompareRule, Form } from "devextreme-react/data-grid";
import { ButtonItem, Item } from "devextreme-react/form";
import filter from "lodash/filter";
import find from "lodash/find";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import omit from "lodash/omit";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NEW_USER } from "./constants";
import locale from "./users.locale";
import Styled, { SIZES } from "./users.styled";
import buildPasswordEyeButtonOptions, { changePasswordMode } from "./utils/buildPasswordEyeButtonOptions";
import loginRender from "./utils/loginRender";
import TogglerWithFloatLabel from "Components/scrolledDropdown/togglerWithFloatLabel/togglerWithFloatLabel";
const coloredAreas = [AREA.TEXT, AREA.BACKGROUND];
const defaultEditorOption = {
    placeholder: "",
    height: 44
};
const roleIdKeySelector = (e) => e.Id;
const roleIdTextSelector = (e) => e.Name;
const formElementAttribute = { style: `min-height: ${SIZES.MIN_FORM_HEIGHT}px;` };
const filterBuilderFields = [
    {
        dataField: "Login",
        dataType: "string",
        caption: locale.LOGIN
    },
    {
        dataField: "FullName",
        dataType: "string",
        caption: locale.USER
    },
    {
        dataField: "Email",
        dataType: "string",
        caption: locale.EMAIL
    },
    {
        dataField: "RoleLocale",
        dataType: "string",
        caption: locale.ROLE
    },
    {
        dataField: "Created",
        dataType: "date",
        caption: locale.CREATED
    }
];
export default function Users(props) {
    var _a;
    const formData = useRef({ ...NEW_USER });
    const gridRef = useRef(null);
    const formRef = useRef(null);
    const [newUserEditorMode, setNewUserEditorMode] = useState(true);
    const [selected, setSelected] = useState([]);
    useEffect(() => {
        !isEmpty(selected) && setSelected([]);
        clearSelection(gridRef.current);
        props.onInitialized && props.onInitialized();
    }, [props.onInitialized]);
    const resetFormTemporaryData = useCallback((user) => {
        formData.current = user ? { ...user } : { ...NEW_USER };
        changePasswordMode("Password", gridRef, "password");
        changePasswordMode("RepeatPassword", gridRef, "password");
    }, []);
    const handleCloseEditor = useCallback(() => {
        var _a;
        const instance = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
        setTimeout(() => {
            instance && instance.cancelEditData();
        }, 0);
        resetFormTemporaryData();
    }, [resetFormTemporaryData]);
    const getSelectedUsersWithStatus = (status) => !selected.length ? [] : filter(selected, id => { var _a; return ((_a = find(props.data, ["Id", id])) === null || _a === void 0 ? void 0 : _a.Status) === status; });
    const selectedBlockedUsers = useMemo(() => getSelectedUsersWithStatus(USER_STATUS.BLOCKED), [props.data, selected]);
    const selectedActiveUsers = useMemo(() => getSelectedUsersWithStatus(USER_STATUS.ACTIVE), [props.data, selected]);
    const handleDelete = useCallback(() => {
        !isEmpty(selected) && props.onDeleted && props.onDeleted(selected);
    }, [selected]);
    const handleBlock = useCallback(() => {
        !isEmpty(selectedActiveUsers) && props.onBlocked && props.onBlocked(selectedActiveUsers);
    }, [selectedActiveUsers]);
    const handleUnblock = useCallback(() => {
        !isEmpty(selectedBlockedUsers) && props.onUnblocked && props.onUnblocked(selectedBlockedUsers);
    }, [selectedBlockedUsers]);
    const handleRowClick = useCallback((e) => {
        var _a;
        setNewUserEditorMode(false);
        resetFormTemporaryData(e.data);
        const instance = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
        instance && instance.editRow(e.rowIndex);
    }, []);
    const statusColumnChildren = useMemo(() => props.userStatusDictionary
        ? [
            React.createElement(Lookup, { key: "lookup", dataSource: props.userStatusDictionary, valueExpr: "Id", displayExpr: "Name" }),
            React.createElement(HeaderFilter, { key: "headerFilter", dataSource: map(props.userStatusDictionary, e => ({ text: e.Name, value: e.Id })) })
        ]
        : undefined, [props.userStatusDictionary]);
    const roleColumnChildren = useMemo(() => props.roles
        ? [
            React.createElement(Lookup, { key: "lookup", dataSource: props.roles, displayExpr: "Name", valueExpr: "Name" }),
            React.createElement(HeaderFilter, { key: "headerFilter", dataSource: map(props.roles, e => ({
                    text: e.Name,
                    value: e.Name
                })) })
        ]
        : undefined, [props.roles]);
    const statusCellRender = useCallback((e) => {
        var _a;
        return e.displayValue && (React.createElement(ColorStyledBlock, { areas: coloredAreas, className: "pt-0_25 pb-0_25 pl-0_75 pr-0_75", color: userStatusColorMap[e.data.Status] }, (_a = find(props.userStatusDictionary, ["Id", e.data.Status])) === null || _a === void 0 ? void 0 : _a.Name));
    }, [props.userStatusDictionary]);
    const gridData = useMemo(() => props.data
        ? map(props.data, item => ({ ...item, Password: "", RepeatPassword: "" }))
        : props.data, [props.data]);
    const handleEditRoleIdCellRender = useCallback((cellInfo) => {
        const setEditedValue = (roleId) => {
            var _a;
            if (formData.current && formData.current.RoleId !== roleId) {
                formData.current.RoleId = roleId;
                formData.current.RoleLocale = ((_a = find(props.roles, ["Id", roleId])) === null || _a === void 0 ? void 0 : _a.Name) || null;
                cellInfo.setValue && cellInfo.setValue(roleId);
                cellInfo.component.repaint();
            }
        };
        return (React.createElement(ScrolledDropdown, { items: props.roles, keySelector: roleIdKeySelector, textSelector: roleIdTextSelector, onSelectedChanged: setEditedValue, selectedKey: formData.current.RoleId, className: "dropdown-toggle-flex-space-between d-grid", maxWidth: "100%", label: locale.ROLE, caption: "", togglerHeight: 44, togglerComponent: React.createElement(TogglerWithFloatLabel, null) }));
    }, [props.roles]);
    const handleSetSelectedIfHasAccess = useCallback((data) => {
        setSelected(map(data, "Id"));
    }, []);
    const handleRowUpdating = useCallback((e) => {
        const oldData = find(props.data, ["Id", e.oldData.Id]);
        const resultData = omit({
            ...e.oldData,
            ...e.newData,
            Password: e.newData.Password || (oldData === null || oldData === void 0 ? void 0 : oldData.Password) || ""
        }, "RepeatPassword");
        props.onSaveUser &&
            Promise.resolve(props.onSaveUser(resultData)).then((promiseResult) => {
                var _a;
                if (((_a = promiseResult === null || promiseResult === void 0 ? void 0 : promiseResult.response) === null || _a === void 0 ? void 0 : _a.Id) !== undefined) {
                    handleCloseEditor();
                }
            });
        resetFormTemporaryData();
        e.cancel = true;
    }, [props.data, props.onSaveUser, handleCloseEditor]);
    const handleRowInserting = useCallback((e) => {
        const resultData = omit({ ...NEW_USER, ...e.data }, "RepeatPassword");
        props.onSaveUser &&
            Promise.resolve(props.onSaveUser(resultData)).then((promiseResult) => {
                var _a;
                if (((_a = promiseResult === null || promiseResult === void 0 ? void 0 : promiseResult.response) === null || _a === void 0 ? void 0 : _a.Id) !== undefined)
                    handleCloseEditor();
            });
        resetFormTemporaryData();
        e.cancel = true;
    }, [props.onSaveUser, handleCloseEditor]);
    const toolbar = useMemo(() => (React.createElement(Toolbar, null,
        React.createElement(ToolbarItem, { name: "searchPanel", location: "before", cssClass: "datagrid-toolbar-search-panel" }),
        (props.canDelete || props.canEdit) && (React.createElement(ToolbarItem, { location: "after", component: () => (React.createElement("div", { className: "d-flex with-table-toolbar-gap justify-content-center" },
                props.canEdit && (React.createElement(React.Fragment, null,
                    React.createElement(Button, { className: "btn-text btn-h-m", onClick: handleBlock, disabled: !selectedActiveUsers.length },
                        React.createElement("i", { className: "icon-lock pr-0_75" }),
                        locale.BLOCK),
                    React.createElement(Button, { className: "btn-text btn-h-m", onClick: handleUnblock, disabled: !selectedBlockedUsers.length },
                        React.createElement("i", { className: "icon-lock-slash pr-0_75" }),
                        locale.UNBLOCK))),
                props.canDelete && (React.createElement(YesNoConfirmation, { onConfirm: handleDelete, title: locale.getConfirmDeleteText(selected.length), disabled: !selected.length },
                    React.createElement(Button, { className: "btn-text-red btn-h-m", disabled: !selected.length },
                        React.createElement("i", { className: "icon-trash pr-0_75" }),
                        locale.DELETE))))) })),
        props.canEdit && (React.createElement(ToolbarItem, { widget: "dxButton", options: {
                icon: "add",
                elementAttr: { class: "btn-primary btn-with-icon btn-h-m" },
                text: locale.CREATE,
                onClick: () => {
                    var _a;
                    setNewUserEditorMode(true);
                    const instance = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
                    resetFormTemporaryData();
                    instance && instance.addRow();
                }
            } })))), [props.canDelete, props.canEdit, selectedBlockedUsers, selectedActiveUsers, selected]);
    const gridComponent = useMemo(() => statusColumnChildren && roleColumnChildren && gridData ? (React.createElement(Styled.DataGridBase, { gridRef: gridRef, key: "users", data: gridData, onRowClick: props.canEdit ? handleRowClick : undefined, onSetSelected: props.canDelete || props.canEdit ? handleSetSelectedIfHasAccess : undefined, onRowUpdating: props.canEdit ? handleRowUpdating : undefined, onRowInserting: props.canEdit ? handleRowInserting : undefined, searchPanelOn: true, filterBuilderFields: filterBuilderFields },
        toolbar,
        props.canEdit && (React.createElement(Editing, { mode: "popup", allowAdding: true },
            React.createElement(Form, { labelLocation: "top", elementAttr: formElementAttribute, labelMode: "floating", colCount: 2, showColonAfterLabel: false, focusStateEnabled: false, ref: formRef },
                React.createElement(Item, { key: 0, itemType: "group", cssClass: "with-user-form-caption-height", colSpan: 2, colCount: 5 },
                    React.createElement(Item, { colSpan: 4, horizontalAlignment: "left", cssClass: "form-group-content-hidden", itemType: "group", caption: newUserEditorMode ? locale.CREATING : locale.EDITING }),
                    React.createElement(ButtonItem, { buttonOptions: {
                            icon: "close",
                            elementAttr: { class: "no-default-colors no-border animate-hover-color" },
                            height: SIZES.FORM_CAPTION_HEIGHT,
                            width: SIZES.FORM_CAPTION_HEIGHT,
                            onClick: handleCloseEditor
                        }, cssClass: "form-group-content-hidden d-flex justify-content-flex-end v-center-items with-user-form-caption-height" })),
                React.createElement(Item, { key: 1, itemType: "group", horizontalAlignment: "left" },
                    React.createElement(Item, { dataField: "Login", editorOptions: {
                            showClearButton: true,
                            readOnly: !newUserEditorMode,
                            ...defaultEditorOption
                        } }),
                    React.createElement(Item, { dataField: "FullName", editorOptions: { showClearButton: true, ...defaultEditorOption } }),
                    React.createElement(Item, { dataField: "Email", editorOptions: { showClearButton: true, ...defaultEditorOption } }),
                    React.createElement(Item, { itemType: "group" },
                        React.createElement(Item, { dataField: "RoleId", editorOptions: defaultEditorOption }))),
                React.createElement(Item, { key: 2, itemType: "group", horizontalAlignment: "right" },
                    React.createElement(Item, { key: `Password${formData.current.Id}`, dataField: "Password", editorType: "dxTextBox", editorOptions: {
                            ...buildPasswordEyeButtonOptions("Password", gridRef),
                            ...defaultEditorOption
                        } }),
                    React.createElement(Item, { key: `RepeatPassword${formData.current.Id}`, dataField: "RepeatPassword", editorType: "dxTextBox", editorOptions: {
                            ...buildPasswordEyeButtonOptions("RepeatPassword", gridRef),
                            ...defaultEditorOption
                        } }))))),
        React.createElement(Column, { allowHeaderFiltering: false, dataField: "Login", dataType: "string", caption: locale.LOGIN, cellRender: loginRender, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER }, width: "calc(45% - 155px)" },
            React.createElement(RequiredRule, { type: "required", message: locale.LOGIN_REQUIRED })),
        React.createElement(Column, { allowHeaderFiltering: false, dataField: "FullName", dataType: "string", caption: locale.USER, cellRender: TextCell, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER }, visible: false }),
        React.createElement(Column, { allowHeaderFiltering: false, dataField: "Email", dataType: "string", width: "20%", caption: locale.EMAIL, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER }, cellRender: TextCell },
            React.createElement(RequiredRule, { type: "required", message: locale.EMAIL_REQUIRED }),
            React.createElement(EmailRule, { message: locale.EMAIL_INVALID })),
        React.createElement(Column, { dataField: "RoleId", caption: locale.ROLE, dataType: "number", visible: false, editCellRender: handleEditRoleIdCellRender, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } },
            React.createElement(RequiredRule, { type: "required", message: locale.ROLE_REQUIRED })),
        React.createElement(Column, { dataField: "RoleLocale", dataType: "string", width: "15%", caption: locale.ROLE, cellRender: TextCell, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } }, roleColumnChildren),
        React.createElement(Column, { allowHeaderFiltering: false, dataField: "Created", dataType: "date", width: 155, caption: locale.CREATED, cellRender: DateCell, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } }),
        React.createElement(Column, { dataField: "Status", dataType: "string", caption: locale.STATUS, width: "20%", cellRender: statusCellRender, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } }, statusColumnChildren),
        React.createElement(Column, { dataField: "Password", dataType: "string", caption: locale.PASSWORD, cellRender: TextCell, visible: false, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } }, newUserEditorMode && (React.createElement(RequiredRule, { type: "required", message: locale.PASSWORD_REQUIRED }))),
        React.createElement(Column, { dataField: "RepeatPassword", dataType: "string", caption: locale.REPEAT_PASSWORD, cellRender: TextCell, visible: false, editorOptions: { placeholder: locale.FILTER_PLACEHOLDER } },
            newUserEditorMode && (React.createElement(RequiredRule, { type: "required", message: locale.PASSWORD_REQUIRED })),
            React.createElement(CompareRule, { message: locale.PASSWORD_CONFIRMATION_IS_INVALID, comparisonTarget: () => {
                    var _a, _b, _c;
                    const dataGrid = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
                    if (!((_b = dataGrid === null || dataGrid === void 0 ? void 0 : dataGrid.getController("editing")) === null || _b === void 0 ? void 0 : _b.getChanges()))
                        return "";
                    return get(dataGrid.getController("editing").getChanges(), "[0].data.Password", (_c = formData.current) === null || _c === void 0 ? void 0 : _c.Password);
                }, type: "compare" })))) : (React.createElement("div", null)), [
        gridData,
        roleColumnChildren,
        statusColumnChildren,
        toolbar,
        statusCellRender,
        handleEditRoleIdCellRender,
        handleSetSelectedIfHasAccess,
        handleRowClick,
        handleRowUpdating,
        handleRowInserting,
        props.canDelete,
        props.canEdit,
        props.roles,
        newUserEditorMode
    ]);
    return (React.createElement("div", { className: classNames("panel-body-padding stretched-to-fit", props.className) },
        React.createElement(ProgressOverlay, { show: props.inProgress },
            React.createElement("div", { className: "flex-single-column with-gap-16" },
                React.createElement("div", { className: "grid-cols-auto-1fr" },
                    React.createElement("h2", { className: "font-bold" }, locale.USERS)),
                React.createElement("div", { className: "flex-greedy-block" }, ((_a = props.data) === null || _a === void 0 ? void 0 : _a.length) === 0 ? (React.createElement(WelcomePanel, { message: locale.NO_DATA_TO_SHOW, icon: emptyContentIcon })) : (gridComponent))))));
}
